/**
 * NetworkPage — "Network" (Wi-Fi networks) device listing (May 2026 Figma).
 *
 * Toolbar:
 *   [🔍]  Network
 *         N TOTAL                                       [⋯] [+ Add network] [▤]
 *
 * Table columns:
 *   [ ] SSID ↑    SITE    SECURITY PROTOCOL    STATUS    [pen][trash]
 *
 * Empty state (per Figma):
 *   "Network list is empty / Click "Add Network" to begin"
 *
 * Backed by deviceService.listWifiNetworks() / .deleteWifiNetwork().
 * Add/Edit modal is not yet specified in the Figma — the toolbar button is
 * wired with a placeholder click handler that will hook up later.
 */
import { useEffect, useMemo, useState } from 'react';
import { useTenant } from '../context/TenantContext';
import deviceService from '../services/deviceService';
import NetworkDrawer from '../components/network/NetworkDrawer';


function deriveStatus(n) {
  // Backend now returns `status` as "ONLINE" | "OFFLINE".
  const raw = String(
    n.status ?? n.networkStatus ?? (n.isActive === false ? 'OFFLINE' : 'OFFLINE')
  ).toUpperCase();
  if (raw === 'ONLINE' || raw === 'ACTIVE')
    return { key: 'active', label: 'Online', cls: 'cam-status cam-status-active', icon: 'fa-circle' };
  if (raw === 'ERROR' || raw === 'FAILED')
    return { key: 'error', label: 'Error', cls: 'cam-status cam-status-error', icon: 'fa-times' };
  // OFFLINE / INACTIVE / DISABLED / unknown → offline
  return { key: 'offline', label: 'Offline', cls: 'cam-status cam-status-offline', icon: 'fa-circle-notch' };
}

export default function NetworkPage() {
  const { orgId, siteId, sites: tenantSites } = useTenant();

  const [rows, setRows]             = useState([]);
  const [loading, setLoading]       = useState(false);
  const [error, setError]           = useState(null);
  const [search, setSearch]         = useState('');
  const [showSearch, setShowSearch] = useState(false);
  const [selected, setSelected]     = useState(() => new Set());
  const [sort, setSort]             = useState({ key: 'ssid', asc: true });
  const [refreshTick, setRefreshTick] = useState(0);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editing, setEditing]       = useState(null);  // null = create
  const refreshList = () => setRefreshTick((t) => t + 1);

  // siteId → siteName lookup (Wi-Fi DTO usually carries siteId only).
  const sitesById = useMemo(() => {
    const m = new Map();
    (tenantSites || []).forEach((s) => {
      const id = s.siteId ?? s.id;
      const name = s.siteName ?? s.name ?? `Site ${id}`;
      m.set(id, name);
      m.set(String(id), name);
    });
    return m;
  }, [tenantSites]);

  useEffect(() => {
    if (!orgId) { setRows([]); return; }
    let cancelled = false;
    setLoading(true);
    setError(null);
    deviceService.listWifiNetworks(orgId, siteId || undefined)
      .then((data) => {
        if (cancelled) return;
        const list = Array.isArray(data) ? data : (data?.content || data?.items || []);
        setRows(list);
      })
      .catch((e) => {
        if (!cancelled) setError(e?.response?.data?.message ?? e.message ?? 'Failed to load networks');
        if (!cancelled) setRows([]);
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [orgId, siteId, refreshTick]);

  // Light field-name harmonization for sorting / search.
  const view = useMemo(() => rows.map((n) => {
    const siteId = n.siteId ?? n.site?.siteId ?? n.site?.id ?? null;
    const siteName = n.siteName ?? n.site?.siteName ?? n.site?.name
      ?? (siteId != null ? (sitesById.get(siteId) || sitesById.get(String(siteId))) : '')
      ?? '';
    return {
      id: n.networkId ?? n.wifiNetworkId ?? n.id,
      raw: n,
      ssid: n.ssid ?? n.networkName ?? n.name ?? '',
      siteId,
      siteName,
      securityProtocol: n.securityProtocol ?? n.securityType ?? n.security ?? n.authType ?? '',
      status: deriveStatus(n),
    };
  }), [rows, sitesById]);

  const total = view.length;

  const filteredSorted = useMemo(() => {
    const q = search.trim().toLowerCase();
    const base = !q ? view : view.filter((n) =>
      [n.ssid, n.siteName, n.securityProtocol, n.status.label].filter(Boolean).join(' ').toLowerCase().includes(q)
    );
    return [...base].sort((a, b) => {
      const get = (n) => {
        switch (sort.key) {
          case 'ssid':       return n.ssid || '';
          case 'siteName':   return n.siteName || '';
          case 'securityProtocol': return n.securityProtocol || '';
          case 'status':     return n.status.label || '';
          default:           return '';
        }
      };
      return (sort.asc ? 1 : -1) * String(get(a)).localeCompare(String(get(b)));
    });
  }, [view, search, sort]);

  const toggleSort = (key) =>
    setSort((p) => (p.key === key ? { key, asc: !p.asc } : { key, asc: true }));
  const sortIcon = (key) =>
    sort.key === key ? (sort.asc ? 'fa-sort-up' : 'fa-sort-down') : '';

  const toggleRow = (id) => setSelected((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });
  const toggleAll = () => setSelected((prev) =>
    prev.size === filteredSorted.length ? new Set() : new Set(filteredSorted.map((n) => n.id))
  );

  const handleAdd = () => {
    setEditing(null);
    setDrawerOpen(true);
  };
  const handleEdit = (n) => {
    setEditing(n.raw);
    setDrawerOpen(true);
  };

  const handleDelete = async (n) => {
    // eslint-disable-next-line no-alert
    if (!window.confirm(`Delete network "${n.ssid}"?`)) return;
    try {
      await deviceService.deleteWifiNetwork(n.id, orgId);
      refreshList();
      setSelected((prev) => { const x = new Set(prev); x.delete(n.id); return x; });
    } catch (e) {
      // eslint-disable-next-line no-alert
      alert(e?.response?.data?.message ?? e.message ?? 'Failed to delete network');
    }
  };

  return (
    <>
      <div className="sp-toolbar">
        <div className="sp-toolbar-left">
          <button
            type="button"
            className="cm-icon-btn"
            title="Search"
            aria-label="Search networks"
            onClick={() => setShowSearch((v) => !v)}
          >
            <i className="fas fa-search"></i>
          </button>
          <div className="sp-heading">
            <h1>Network</h1>
            <div className="up-summary">
              <span className="up-summary-item"><strong>{total}</strong> TOTAL</span>
            </div>
            {showSearch && (
              <div className="cm-search-inline">
                <i className="fas fa-search"></i>
                <input
                  type="text"
                  placeholder="Search networks..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  autoFocus
                />
              </div>
            )}
          </div>
        </div>
        <div className="sp-toolbar-right">
          <button type="button" className="cm-icon-btn cm-icon-btn-circle" title="More" aria-label="More">
            <i className="fas fa-ellipsis-h"></i>
          </button>
          <button type="button" className="btn-primary-pill" onClick={handleAdd}>
            <i className="fas fa-plus"></i> Add network
          </button>
          <button type="button" className="cm-icon-btn sp-view-toggle" title="Toggle view" aria-label="Toggle view">
            <i className="fas fa-table-list"></i>
          </button>
        </div>
      </div>

      <div className="cameras-table-card">
        <table className="data-table cameras-table">
          <thead>
            <tr>
              <th style={{ width: 36 }}>
                <input
                  type="checkbox"
                  checked={filteredSorted.length > 0 && selected.size === filteredSorted.length}
                  onChange={toggleAll}
                  aria-label="Select all"
                />
              </th>
              <th>
                <button className="data-table-sort" onClick={() => toggleSort('ssid')}>
                  SSID {sortIcon('ssid') && <i className={`fas ${sortIcon('ssid')}`}></i>}
                </button>
              </th>
              <th><button className="data-table-sort" onClick={() => toggleSort('siteName')}>SITE</button></th>
              <th><button className="data-table-sort" onClick={() => toggleSort('securityProtocol')}>SECURITY PROTOCOL</button></th>
              <th><button className="data-table-sort" onClick={() => toggleSort('status')}>STATUS</button></th>
              <th style={{ width: 90 }}></th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr>
                <td colSpan={6} style={{ textAlign: 'center', padding: 30, color: 'var(--color-text-muted)' }}>
                  <i className="fas fa-spinner fa-spin" /> Loading networks…
                </td>
              </tr>
            )}
            {!loading && error && (
              <tr>
                <td colSpan={6} style={{ textAlign: 'center', padding: 30, color: '#fca5a5' }}>{error}</td>
              </tr>
            )}
            {!loading && !error && filteredSorted.length === 0 && (
              <tr>
                <td colSpan={6} style={{ textAlign: 'center', padding: 40, color: 'var(--color-text-muted)' }}>
                  <div>Network list is empty</div>
                  <div style={{ marginTop: 4 }}>
                    Click <strong>“Add Network”</strong> to begin
                  </div>
                </td>
              </tr>
            )}
            {!loading && !error && filteredSorted.map((n) => (
              <tr key={n.id}>
                <td>
                  <input
                    type="checkbox"
                    checked={selected.has(n.id)}
                    onChange={() => toggleRow(n.id)}
                    aria-label={`Select ${n.ssid}`}
                  />
                </td>
                <td>{n.ssid || <span className="up-muted">—</span>}</td>
                <td>{n.siteName || <span className="up-muted">—</span>}</td>
                <td>{n.securityProtocol || <span className="up-muted">—</span>}</td>
                <td>
                  <span className={n.status.cls}>
                    <i className={`fas ${n.status.icon}`}></i>
                    {n.status.label}
                  </span>
                </td>
                <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                  <button className="row-action" title="Edit" onClick={() => handleEdit(n)}>
                    <i className="fas fa-pencil-alt"></i>
                  </button>
                  <button className="row-action row-action-danger" title="Delete" onClick={() => handleDelete(n)}>
                    <i className="fas fa-trash"></i>
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <NetworkDrawer
        open={drawerOpen}
        orgId={orgId}
        initial={editing}
        sites={tenantSites}
        onClose={() => setDrawerOpen(false)}
        onSaved={() => { setDrawerOpen(false); refreshList(); }}
      />
    </>
  );
}


